<template>
  <div
    id="dev-CreditCardRegistrationForm"
    class="credit-card-registration-form"
  >
    <div class="form-body mt-3">
      <!-- Card Number -->
      <div class="form-group">
        <BaseInput
          id="card-number"
          v-model="card.number"
          :mask="'#### #### #### ####'"
          type="text"
          autocomplete="cc-number"
          :label="$t('credit_card_registration.card_number')"
          :error="errors.number"
          placeholder="0000 0000 0000 0000"
          variant="primary"
          input-wrapper-class="input-with-icon"
        >
          <template #append>
            <div
              v-if="cardBrand"
              class="card-brand-icon"
            >
              <span>{{ cardBrand }}</span>
            </div>
          </template>
        </BaseInput>
      </div>

      <!-- Expiry and CVV -->
      <div class="form-row">
        <div class="form-group col">
          <BaseInput
            id="card-expiry"
            v-model="card.expiry"
            :mask="'##/##'"
            type="text"
            autocomplete="cc-exp"
            :label="$t('credit_card_registration.expiry_date')"
            :error="errors.expiry"
            placeholder="MM/AA"
            variant="primary"
          />
        </div>

        <div class="form-group col">
          <BaseInput
            id="card-cvv"
            v-model="card.cvs"
            :mask="'####'"
            type="text"
            autocomplete="cc-csc"
            :label="'CVV'"
            :error="errors.cvs"
            placeholder="123"
            maxlength="4"
            variant="primary"
          />
        </div>
      </div>

      <!-- Card Holder Name -->
      <div class="form-group">
        <BaseInput
          id="card-name"
          v-model="card.name"
          type="text"
          autocomplete="cc-name"
          spellcheck="false"
          :label="$t('credit_card_registration.cardholder_name')"
          :error="errors.name"
          :placeholder="$t('credit_card_registration.cardholder_name_placeholder')"
          input-class="text-uppercase"
          variant="primary"
        />
      </div>

      <div
        v-if="errorMessage"
        class="alert alert-danger"
        role="alert"
      >
        {{ errorMessage }}
      </div>
    </div>

    <div class="form-actions">
      <BaseButton
        variant="primary"
        size="sm"
        block
        constrained
        :disabled="loading"
        @click="registerCard"
      >
        <span
          v-if="loading"
          class="spinner-border spinner-border-sm me-2"
          role="status"
          aria-hidden="true"
        ></span>
        {{ loading ? $t('common.saving') : $t('credit_card_registration.button') }}
      </BaseButton>

      <BaseButton
        variant="outline-secondary"
        size="sm"
        block
        constrained
        class="mt-2"
        :disabled="loading"
        @click="$emit('cancel')"
      >
        {{ $t('common.cancel') }}
      </BaseButton>
    </div>

    <!-- Trust badges -->
    <div class="trust-badges">
      <div class="badge-item">
        <font-awesome-icon icon="lock" />
        <span>{{ $t('common.secure_payment') }}</span>
      </div>
      <div class="badge-item">
        <font-awesome-icon icon="shield-alt" />
        <span>SSL</span>
      </div>
    </div>
  </div>
</template>

<script>
  import API from '@/services/api'
  import { sessionRecorder, UserEvent } from '@/services/sessionRecorder'
  import BaseButton from '@/components/ui/BaseButton.vue'
  import BaseInput from '@/components/ui/BaseInput.vue'

  const TAG_CREDIT_CARD_REGISTRATION = 'credit_card_registration_inline'

  export default {
    name: 'CreditCardRegistrationForm',
    components: {
      BaseButton,
      BaseInput
    },
    props: {
      user: {
        type: Object,
        required: true
      }
    },
    emits: ['payment-added', 'cancel'],
    data() {
      return {
        card: {
          number: '',
          expiry: '',
          cvs: '',
          name: ''
        },
        errors: {
          number: null,
          expiry: null,
          cvs: null,
          name: null
        },
        errorMessage: '',
        loading: false,
        cardBrand: null
      }
    },
    watch: {
      card: {
        handler() {
          // Clear errors when user types
          this.errors = {
            number: null,
            expiry: null,
            cvs: null,
            name: null
          }
          this.errorMessage = ''
        },
        deep: true
      },
      'card.number'(newValue) {
        // Detect card brand
        const cleaned = newValue.replace(/\s/g, '')
        this.cardBrand = this.getFlagTemporary(cleaned)
      }
    },
    mounted() {
      sessionRecorder.addUserEvent(new UserEvent(TAG_CREDIT_CARD_REGISTRATION))
    },
    methods: {
      validateCard() {
        let isValid = true
        this.errors = {
          number: null,
          expiry: null,
          cvs: null,
          name: null
        }

        // Validate card number
        const cardNumber = this.card.number.replace(/\s/g, '')
        if (!cardNumber) {
          this.errors.number = this.$t('credit_card_registration.errors.number_required')
          isValid = false
        } else if (cardNumber.length < 16) {
          this.errors.number = this.$t('credit_card_registration.errors.number_invalid')
          isValid = false
        }

        // Validate expiry
        if (!this.card.expiry) {
          this.errors.expiry = this.$t('credit_card_registration.errors.expiry_required')
          isValid = false
        } else {
          const [month, year] = this.card.expiry.split('/')
          if (!month || !year || month.length !== 2 || year.length !== 2) {
            this.errors.expiry = this.$t('credit_card_registration.errors.expiry_invalid')
            isValid = false
          } else {
            const monthNum = parseInt(month)
            const yearNum = parseInt(`20${year}`)
            const now = new Date()
            const currentYear = now.getFullYear()
            const currentMonth = now.getMonth() + 1

            if (monthNum < 1 || monthNum > 12) {
              this.errors.expiry = this.$t('credit_card_registration.errors.month_invalid')
              isValid = false
            } else if (yearNum < currentYear || (yearNum === currentYear && monthNum < currentMonth)) {
              this.errors.expiry = this.$t('credit_card_registration.errors.card_expired')
              isValid = false
            }
          }
        }

        // Validate CVV
        if (!this.card.cvs) {
          this.errors.cvs = this.$t('credit_card_registration.errors.cvv_required')
          isValid = false
        } else if (this.card.cvs.length < 3) {
          this.errors.cvs = this.$t('credit_card_registration.errors.cvv_invalid')
          isValid = false
        }

        // Validate name
        if (!this.card.name) {
          this.errors.name = this.$t('credit_card_registration.errors.name_required')
          isValid = false
        }

        return isValid
      },

      async registerCard() {
        if (!this.validateCard()) {
          return
        }

        this.loading = true

        try {
          const response = await API.post('credit_card/register', {
            name: this.card.name.toUpperCase(),
            number: this.card.number.replace(/\s/g, ''),
            cvs: this.card.cvs,
            expiration_date: this.card.expiry
          })

          if (response.data && response.data.success) {
            const creditCardId = response.data.credit_card_id
            const flag = this.getFlagTemporary(this.card.number.replace(/\s/g, ''))
            const newCard = {
              id: creditCardId,
              number: this.card.number.replace(/\s/g, '').slice(-4),
              name: this.card.name.toUpperCase(),
              expiration_date: this.card.expiry,
              cvs: this.card.cvs,
              flag,
              default: true
            }
            const creditCards = (this.user.credit_cards || []).map(c => ({ ...c, default: false }))
            creditCards.push(newCard)
            const updatedUser = {
              ...this.user,
              credit_cards: creditCards,
              default_card: newCard
            }

            this.$emit('payment-added', {
              type: 'card',
              user: updatedUser
            })
          } else {
            this.errorMessage = response.data?.msg || this.$t('credit_card_registration.errors.register_error')

            if (response.data?.field) {
              this.errors = { number: null, expiry: null, cvs: null, name: null }
              if (response.data.field === 'number') this.errors.number = response.data.msg
              if (response.data.field === 'expiration_date') this.errors.expiry = response.data.msg
              if (response.data.field === 'cvv') this.errors.cvs = response.data.msg
              if (response.data.field === 'name') this.errors.name = response.data.msg
            }
          }
        } catch (error) {
          console.error('Error registering card:', error)
          this.errorMessage =
            error.response?.data?.message || this.$t('credit_card_registration.errors.register_error_retry')
        } finally {
          this.loading = false
        }
      },

      getFlagTemporary(creditCardNumber) {
        const firstDigit = creditCardNumber.charAt(0)
        const firstTwoDigits = creditCardNumber.substring(0, 2)

        if (firstDigit === '4') {
          return 'Visa'
        } else if (firstTwoDigits >= '51' && firstTwoDigits <= '55') {
          return 'Mastercard'
        } else if (firstTwoDigits === '34' || firstTwoDigits === '37') {
          return 'Amex'
        } else if (firstTwoDigits >= '36' && firstTwoDigits <= '39') {
          return 'Diners'
        } else if (firstTwoDigits === '50' || (firstTwoDigits >= '56' && firstTwoDigits <= '69')) {
          return 'Elo'
        }

        return null
      }
    }
  }
</script>

<style scoped lang="scss">
  .credit-card-registration-form {
    padding: 0;
    background: white;
    border-radius: 8px;
  }

  .form-header {
    text-align: center;
    margin-bottom: 1rem;
  }

  .card-logo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    border-radius: 50%;
    margin-bottom: 0.5rem;
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.12);
  }

  .card-icon {
    font-size: 1.25rem;
    color: #1565c0;
  }

  .form-title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 0.25rem;
  }

  .form-subtitle {
    font-size: 0.75rem;
    color: #666;
    margin: 0;
  }

  .form-body {
    margin-bottom: 1rem;
  }

  .form-group {
    margin-bottom: 0.75rem;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
  }

  .col {
    min-width: 0;
  }

  label {
    display: block;
    font-size: 0.75rem;
    font-weight: 500;
    color: #2c3e50;
    margin-bottom: 0.375rem;
  }

  :deep(.input-with-icon) {
    position: relative;
  }

  .form-control {
    width: 100%;
    padding: 0.625rem 0.75rem;
    font-size: 0.875rem;
    border: 1.5px solid #e0e0e0;
    border-radius: 6px;
    transition: all 0.2s ease;

    &:focus {
      outline: none;
      border-color: #1565c0;
      box-shadow: 0 0 0 2px rgba(21, 101, 192, 0.08);
    }

    &.is-invalid {
      border-color: #dc3545;

      &:focus {
        box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.08);
      }
    }

    &::placeholder {
      color: #999;
    }

    &.text-uppercase {
      text-transform: uppercase;
    }
  }

  :deep(.card-brand-icon) {
    position: absolute;
    right: 0.625rem;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.6875rem;
    font-weight: 600;
    color: #1565c0;
    background: #e3f2fd;
    padding: 0.1875rem 0.375rem;
    border-radius: 3px;
  }

  .invalid-feedback {
    display: block;
    margin-top: 0.25rem;
    font-size: 0.6875rem;
    color: #dc3545;
  }

  .alert {
    padding: 0.625rem 0.75rem;
    border-radius: 6px;
    font-size: 0.75rem;
    margin-top: 0.75rem;
  }

  .alert-danger {
    color: #842029;
    background-color: #f8d7da;
    border: 1px solid #f5c2c7;
  }

  .form-actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }

  .btn {
    padding: 0.5rem 1rem;
    font-size: var(--fs-xs);
    font-weight: var(--fw-medium);
    letter-spacing: -0.01em;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .btn-primary {
    background-color: #f58a48 !important;
    border-color: #f58a48 !important;
    color: white;

    &:hover:not(:disabled) {
      background-color: #e67e3e !important;
      border-color: #e67e3e !important;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(245, 138, 72, 0.2);
    }
  }

  .btn-outline-secondary {
    background: white;
    color: #6c757d;
    border: 1.5px solid #dee2e6;

    &:hover:not(:disabled) {
      background: #f8f9fa;
      border-color: #adb5bd;
    }
  }

  .btn-block {
    width: 100%;
    max-width: 250px;
  }

  .mt-2 {
    margin-top: 0.5rem;
  }

  .me-2 {
    margin-right: 0.5rem;
  }

  .spinner-border {
    display: inline-block;
    width: 0.875rem;
    height: 0.875rem;
    vertical-align: text-bottom;
    border: 0.125em solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: spinner-border 0.75s linear infinite;
  }

  .spinner-border-sm {
    width: 0.75rem;
    height: 0.75rem;
    border-width: 0.1em;
  }

  @keyframes spinner-border {
    to {
      transform: rotate(360deg);
    }
  }

  .trust-badges {
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin-top: 1rem;
    padding-top: 0.75rem;
    border-top: 1px solid #e0e0e0;
  }

  .badge-item {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.6875rem;
    color: #666;

    svg {
      color: #1565c0;
      font-size: 0.75rem;
    }
  }

  @media (max-width: 576px) {
    .credit-card-registration-form {
      padding: 0;
    }

    .form-title {
      font-size: 0.875rem;
    }

    .btn {
      padding: 0.5rem 0.875rem;
      font-size: 0.75rem;
    }
  }
</style>
